import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  FileText,
  Search,
  Download,
  ExternalLink,
  Filter,
  RefreshCw,
  User,
  Calendar,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import Pagination from '../../Components/common/Pagination';
import { TableSkeleton } from '../../Components/common/Skeleton';

const ReportsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });

  const fetchReports = async (page = 1, limit = 10, search = '', type = '', source = '') => {
    setLoading(true);
    try {
      const response = await api.get('/leads/reports', {
        params: {
          page,
          limit,
          search,
          type,
          source
        }
      });




      if (response.data?.success) {
        const responseData = response.data.data;




        if (responseData && responseData.data !== undefined && responseData.pagination) {
          const reportsArray = Array.isArray(responseData.data) ? responseData.data : [];


          setReports(reportsArray);
          setPagination(responseData.pagination);
          setCurrentPage(page);
          if (reportsArray.length === 0) {
            toast('No reports found with documents', { icon: 'ℹ️' });
          }
        } else {
          console.error('Invalid response structure:', responseData);
          console.error('responseData.data is:', responseData?.data);
          console.error('responseData.pagination is:', responseData?.pagination);
          setReports([]);
          toast.error('Failed to parse reports list - Invalid structure');
        }
      } else {
        console.error('API returned unsuccessful:', response.data);
        setReports([]);
        toast.error('API returned unsuccessful response');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      setReports([]);

      // Better error messaging
      if (error.response?.status === 401) {
        toast.error('Unauthorized - Please login again');
      } else if (error.response?.status === 403) {
        toast.error('Access Denied - You do not have permission to view reports');
      } else if (error.response?.status === 404) {
        toast.error('Reports endpoint not found');
      } else {
        toast.error(`Failed to load reports feed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchReports(1, pageSize, debouncedSearch, typeFilter, sourceFilter);
  }, [pageSize, debouncedSearch, typeFilter, sourceFilter]);

  const handlePageChange = (newPage) => {
    fetchReports(newPage, pageSize, debouncedSearch, typeFilter, sourceFilter);
  };

  const docTypes = [
    { value: 'medical_report', label: 'Medical Report' },
    { value: 'id_proof', label: 'ID Proof' },
    { value: 'ayushman_card', label: 'Ayushman Card' },
    { value: 'consent_form', label: 'Consent Form' }
  ];

  const uploadSources = [
    { value: 'CC', label: 'CC' },
    { value: 'Patient', label: 'Patient' },
    { value: 'Hospital', label: 'Hospital' }
  ];

  const filteredReports = reports;

  const getDocTypeBadgeColor = (type) => {
    switch (type) {
      case 'medical_report':
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'id_proof':
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case 'ayushman_card':
        return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'consent_form':
        return 'bg-amber-50 text-amber-700 border-amber-100';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-100';
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">


      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">Clinical & Document Reports</h2>
          <p className="text-slate-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Consolidated feed of all diagnostic, clinical, and identity documents • {pagination.total} total records</p>
        </div>
        <div className="flex items-center gap-3 self-start md:self-auto flex-wrap">
          <select
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value))}
            className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-slate-900/20"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
          <button
            onClick={() => fetchReports(currentPage, pageSize, debouncedSearch, typeFilter, sourceFilter)}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-md"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Filter and Search Panel */}
      <section className="bg-white rounded-[24px] sm:rounded-[32px] p-6 shadow-xl shadow-slate-200/40 border border-slate-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search patient or file name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#143E78]/20 focus:border-[#143E78] transition-all"
            />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#143E78]/20 focus:border-[#143E78] transition-all appearance-none cursor-pointer"
            >
              <option value="">All Document Types</option>
              {docTypes.map(item => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>

          {/* Source Filter */}
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#143E78]/20 focus:border-[#143E78] transition-all appearance-none cursor-pointer"
            >
              <option value="">All Upload Sources</option>
              {uploadSources.map(item => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>

        </div>
      </section>

      {/* Main Reports Feed */}
      <section className="bg-white rounded-[24px] sm:rounded-[36px] p-6 sm:p-8 shadow-2xl shadow-slate-200/40 border border-slate-100 flex flex-col">
        {loading ? (
          <TableSkeleton rows={pageSize} />
        ) : filteredReports.length > 0 ? (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Patient Details</th>
                  <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Document Name</th>
                  <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Type</th>
                  <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Source</th>
                  <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Uploaded Date</th>
                  <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Assigned CC</th>
                  <th className="pb-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredReports.map(report => (
                  <tr key={report._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-4">
                      <div className="text-sm font-black text-slate-900">{report.patientName}</div>
                      <div className="text-[10px] font-semibold text-slate-400">{report.bookingId}</div>
                    </td>
                    <td className="py-4 max-w-[200px] truncate">
                      <div className="text-xs font-bold text-slate-800 truncate" title={report.name}>
                        {report.name || 'Unnamed Document'}
                      </div>
                    </td>
                    <td className="py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 text-[9px] font-black uppercase tracking-wider border rounded-lg ${getDocTypeBadgeColor(report.type)}`}>
                        {report.type?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-4">
                      <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-[8px] font-black uppercase tracking-wider rounded-md">
                        {report.source}
                      </span>
                    </td>
                    <td className="py-4 text-xs font-semibold text-slate-500">
                      <div>{new Date(report.uploadedAt).toLocaleDateString()}</div>
                      <div className="text-[10px] text-slate-400">
                        {new Date(report.uploadedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="py-4 text-xs font-bold text-slate-600">{report.assignedCC}</td>
                    <td className="py-4">
                      <div className="flex items-center justify-center gap-2">
                        {/* View/Download link */}
                        <a
                          href={report.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-slate-50 hover:bg-slate-100 border border-slate-100 hover:border-slate-200 text-slate-600 rounded-xl transition-all shadow-sm"
                          title="Open Document"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                        {/* View Lead details */}
                        <button
                          onClick={() => navigate(`/leads/${report.leadId}?menu=leads`)}
                          className="p-2 bg-blue-50 hover:bg-blue-100 border border-blue-100 hover:border-blue-200 text-blue-600 rounded-xl transition-all shadow-sm"
                          title="View Lead File"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-100">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider">No Documents Found</h4>
            <p className="text-xs text-slate-400 font-semibold mt-1">Try modifying search term or filters.</p>
          </div>
        )}
      </section>

      {/* Pagination Controls */}
      {pagination.total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Displaying {pagination.total > 0 ? (currentPage - 1) * pageSize + 1 : 0}-{Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} records
          </div>

          <div className="w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsList;